import React, { useEffect } from 'react';
import ReactFlow, { 
  Background, 
  Controls, 
  Node, 
  Edge,
  MarkerType,
  useNodesState,
  useEdgesState,
  Position
} from 'reactflow';
import 'reactflow/dist/style.css';
import dagre from 'dagre';

interface VisualizationProps {
  attributes: string;
  dependencies: string;
  reportJson?: any;
}

const nodeWidth = 50;
const nodeHeight = 50;

const getLayoutedElements = (nodes: Node[], edges: Edge[], direction = 'LR') => {
  const dagreGraph = new dagre.graphlib.Graph();
  dagreGraph.setDefaultEdgeLabel(() => ({}));

  dagreGraph.setGraph({ rankdir: direction });

  nodes.forEach((node) => {
    dagreGraph.setNode(node.id, { width: nodeWidth, height: nodeHeight });
  });

  edges.forEach((edge) => {
    dagreGraph.setEdge(edge.source, edge.target);
  });

  dagre.layout(dagreGraph);

  nodes.forEach((node) => {
    const nodeWithPosition = dagreGraph.node(node.id);
    node.targetPosition = direction === 'LR' ? Position.Left : Position.Top;
    node.sourcePosition = direction === 'LR' ? Position.Right : Position.Bottom;

    // We are shifting the dagre node position (anchor=center center) to the top left
    // so it matches the React Flow node anchor point (top left).
    node.position = {
      x: nodeWithPosition.x - nodeWidth / 2,
      y: nodeWithPosition.y - nodeHeight / 2,
    };

    return node;
  });

  return { nodes, edges };
};

const Visualization: React.FC<VisualizationProps> = ({ attributes, dependencies, reportJson }) => {
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);

  // Distinct colors for different complex dependencies
  const COLORS = [
    '#0d6efd', // Blue
    '#6610f2', // Indigo
    '#d63384', // Pink
    '#dc3545', // Red
    '#fd7e14', // Orange
    '#198754', // Green
    '#20c997', // Teal
    '#0dcaf0'  // Cyan
  ];

  // Calculate layout whenever inputs change
  useEffect(() => {
    // Clean regex for splitting attributes
    const attributeList = attributes.split(/[\s,]+/).filter(a => a.length > 0);
    
    // Create initial nodes without position (will be set by dagre)
    const initialNodes: Node[] = attributeList.map((attr, idx) => ({
      id: attr,
      data: { label: attr },
      position: { x: 0, y: 0 },
      style: { 
        width: 40, 
        height: 40, 
        borderRadius: '50%', 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        fontWeight: 'bold',
        backgroundColor: '#fff',
        border: '2px solid #333'
      }
    }));

    // Helper to parse attributes, handling "AB" as ["A", "B"]
    const parseAttrs = (str: string): string[] => {
      // First split by separators
      const parts = str.split(/[\s,]+/).filter(s => s.length > 0);
      const result: string[] = [];
      parts.forEach(p => {
        // If a part is longer than 1 char and uppercase (e.g. "AB"), split it into chars
        // This assumes single-letter attributes as per RDT definition
        if (p.length > 1 && /^[A-Z]+$/.test(p)) {
          result.push(...p.split(''));
        } else {
          result.push(p);
        }
      });
      return result;
    };

    const initialEdges: Edge[] = [];
    // Split dependencies by newline, comma, or semicolon robustly
    const depLines = dependencies
      .replace(/\r\n/g, '\n') // Normalize CRLF
      .replace(/\r/g, '\n')   // Normalize CR
      .split(/[\n,;]+/)       // Split by separators
      .map(line => line.trim()) // Clean up
      .filter(d => d.includes('->') || d.includes('→')); // Only valid deps
      
    console.log('Visualization: Dependency Lines:', depLines);

    depLines.forEach((line, dIdx) => {
      // Normalize arrow
      const normalizedLine = line.replace('→', '->');
      const parts = normalizedLine.split('->');
      if (parts.length !== 2) return; 
      
      const lhs = parts[0].trim();
      const rhs = parts[1].trim();
      
      const lhsAttrs = parseAttrs(lhs);
      const rhsAttrs = parseAttrs(rhs);
      
      const color = COLORS[dIdx % COLORS.length];

      console.log(`Parsing Dep ${dIdx}: LHS=${lhsAttrs}, RHS=${rhsAttrs}, Color=${color}`);

      if (lhsAttrs.length > 1) {
        console.warn(`Creating HUB for ${lhs} -> ${rhs}`);
        // Multi-attribute determinant: Create a hub node
        const hubId = `hub-${dIdx}`;
        
        initialNodes.push({
          id: hubId,
          data: { label: '&' }, 
          position: { x: 0, y: 0 },
          style: { 
            width: 24, 
            height: 24, 
            backgroundColor: color, 
            color: '#fff',
            borderRadius: '50%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            fontSize: '12px',
            fontWeight: 'bold',
            border: 'none',
            boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
          }
        });

        // Edges from LHS to hub (Inputs -> Dashed, Grey)
        lhsAttrs.forEach(a => {
          initialEdges.push({
            id: `e-${a}-${hubId}`,
            source: a,
            target: hubId,
            style: { stroke: '#adb5bd', strokeWidth: 1, strokeDasharray: '5,5' }
          });
        });

        // Edges from hub to RHS (Outputs -> Solid, Colored)
        rhsAttrs.forEach(a => {
          initialEdges.push({
            id: `e-${hubId}-${a}`,
            source: hubId,
            target: a,
            markerEnd: { type: MarkerType.ArrowClosed, color: color },
            style: { stroke: color, strokeWidth: 2 }
          });
        });
      } else if (lhsAttrs.length === 1) {
        // Single attribute determinant
        const source = lhsAttrs[0];
        rhsAttrs.forEach(target => {
          const edgeId = `e-${source}-${target}-${dIdx}`;
          console.log(`Creating edge: ${edgeId} from ${source} to ${target}`);
          initialEdges.push({
            id: edgeId,
            source: source,
            target: target,
            markerEnd: { type: MarkerType.ArrowClosed, color: color },
            style: { stroke: color, strokeWidth: 2 }
          });
        });
      }
    });

    const layouted = getLayoutedElements(initialNodes, initialEdges);

    console.log('Visualization: Nodes:', layouted.nodes);
    console.log('Visualization: Edges:', layouted.edges);

    setNodes([...layouted.nodes]);
    setEdges([...layouted.edges]);
  }, [attributes, dependencies, setNodes, setEdges]);

  return (
    <div className="border rounded shadow-sm" style={{ width: '100%', height: '500px', backgroundColor: '#f8f9fa' }}>
      <ReactFlow
        key={edges.length} // Force re-mount on edge change to ensure rendering
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        nodesConnectable={false}
        fitView
      >
        <Background color="#ccc" gap={20} />
        <Controls />
      </ReactFlow>
      <div className="p-2 small text-muted text-center border-top bg-white rounded-bottom">
        Visualisierung der funktionalen Abhängigkeiten. Hubs (schwarze Punkte) stehen für zusammengesetzte Determinanten.
      </div>
    </div>
  );
};

export default Visualization;
